import { Spinner } from '@components/common/Spinner';
import { ProvisioningCurtain } from '@components/provisioning/ProvisioningCurtain';
import { SELECT_CUSTOMER_ROUTE } from '@constants/routes';
import { toastErrorTranslated } from '@helpers/toast';
import { useProvisioningStatus } from '@hooks/provisioning/useProvisioningStatus';
import { useCompanySettingsActions } from '@hooks/settings/company/useCompanyActions';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useMainSelector } from '@hooks/useMainSelector';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useCurrentUserActions } from '../../hooks/auth/useCurrentUserActions';

// * The lifecycle modal hands the teardown over through router state so the
// * request is issued behind this curtain instead of on the settings page of a
// * company that is about to go inactive.
const shouldStartTeardown = (routeState: unknown): boolean => {
  if (typeof routeState !== 'object' || routeState === null) {
    return false;
  }

  return 'startTeardown' in routeState && routeState.startTeardown === true;
};

export const ProvisioningStatusPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: { currentUser },
  } = useMainSelector();
  const { setSelectedCompany } = useCompanySelector();
  const { fetchCurrentUser } = useCurrentUserActions();
  const { teardownCompany } = useCompanySettingsActions();

  const {
    status,
    stepStates,
    provision,
    isLoading,
    isRetrying,
    retry,
    markDeprovisioning,
    settleDeprovisioning,
  } = useProvisioningStatus(companyId ?? '');

  const [isNavigatingToCompany, setIsNavigatingToCompany] = useState(false);
  const [isTearingDown, setIsTearingDown] = useState(false);
  const hasStartedTeardownRef = useRef(false);

  const handleGoToCompany = useCallback(async () => {
    setIsNavigatingToCompany(true);

    try {
      // * The company was just created, so the Redux-cached list is stale -
      // * refresh it and re-resolve from the returned data rather than the list
      // * captured in this closure.
      const refreshedCompanies = await fetchCurrentUser();
      const company = refreshedCompanies.find(
        (candidate) => candidate.id === companyId,
      );

      if (company) {
        // * The refreshed list has to travel with the selection - the selector's
        // * cached snapshot predates provisioning and still has this company
        // * inactive.
        setSelectedCompany(company, refreshedCompanies);

        return;
      }

      navigate(SELECT_CUSTOMER_ROUTE);
    } catch (error) {
      console.error('[Provisioning] Failed to open the company:', error);
      toastErrorTranslated(error);
    } finally {
      setIsNavigatingToCompany(false);
    }
  }, [companyId, setSelectedCompany, fetchCurrentUser, navigate]);

  const runTeardown = useCallback(async () => {
    if (!companyId) {
      return;
    }

    setIsTearingDown(true);
    // * Flip before awaiting: the request stays open for the whole teardown, so
    // * without this the curtain would keep showing the provisioning result.
    markDeprovisioning();

    try {
      await teardownCompany(companyId);
    } finally {
      setIsTearingDown(false);
      await settleDeprovisioning();
    }
  }, [companyId, markDeprovisioning, settleDeprovisioning, teardownCompany]);

  useEffect(() => {
    // * Only after the initial fetch settles, otherwise it resolves on top of the
    // * optimistic deprovisioning flip and puts the provisioning screen back.
    if (isLoading || hasStartedTeardownRef.current) {
      return;
    }

    if (!shouldStartTeardown(location.state)) {
      return;
    }

    hasStartedTeardownRef.current = true;
    // * Drop the flag from history so a reload does not re-fire the teardown.
    navigate(location.pathname, { replace: true, state: null });
    runTeardown();
  }, [isLoading, location.pathname, location.state, navigate, runTeardown]);

  const handleRetry = useCallback(() => {
    // * A deprovision_failed company must never reach retryProvisioning - that
    // * would re-create resources on a half-torn-down company and re-activate it.
    if (status === 'deprovision_failed') {
      runTeardown();

      return;
    }

    retry();
  }, [status, runTeardown, retry]);

  const handleBack = useCallback(() => {
    navigate(SELECT_CUSTOMER_ROUTE);
  }, [navigate]);

  if (!currentUser?.isSuperAdmin || !companyId) {
    return <Navigate to={SELECT_CUSTOMER_ROUTE} replace />;
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-dvh'>
        <Spinner size='lg' />
      </div>
    );
  }

  return (
    <ProvisioningCurtain
      status={status}
      stepStates={stepStates}
      identifier={provision?.identifier}
      canRetry={status === 'failed' || status === 'deprovision_failed'}
      isRetrying={isRetrying || isTearingDown}
      isNavigatingToCompany={isNavigatingToCompany}
      onGoToCompany={handleGoToCompany}
      onRetry={handleRetry}
      onBack={handleBack}
      onClose={handleBack}
    />
  );
};
